/**
 * Sticky Add-to-Cart Bar v2
 * Shows a fixed ATC bar when the main button scrolls out of view.
 * Syncs with variant changes and handles cart additions.
 */
class StickyAtcBar extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    // Find the main ATC button on the page
    this.mainAtcButton = document.querySelector(
      'product-form-component [ref="addToCartButton"]'
    ) || document.querySelector(
      '.product-information add-to-cart-component button[type="submit"]'
    ) || document.querySelector(
      '.product-information product-form-component button[type="submit"]'
    );

    // Fallback: observe the buy-buttons block or product form
    if (!this.mainAtcButton) {
      this.mainAtcButton = document.querySelector(
        '.product-information .buy-buttons'
      ) || document.querySelector(
        '.product-information product-form-component'
      );
    }

    if (!this.mainAtcButton) return;

    // Track whether the user has scrolled past the ATC button at least once.
    // The sticky bar must NOT appear before the user has seen the real button.
    this._hasSeenButton = false;

    // Setup IntersectionObserver
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // User can see the real ATC button — mark as seen, hide sticky bar
          this._hasSeenButton = true;
          if (this._showRaf) { cancelAnimationFrame(this._showRaf); this._showRaf = null; }
          this.dataset.visible = 'false';
          this.setAttribute('aria-hidden', 'true');
        } else if (this._hasSeenButton) {
          // Button is out of view AND user has seen it before.
          // Only show the sticky bar when the button is ABOVE the viewport
          // (user scrolled PAST/BELOW the cart area). If the button is BELOW
          // the viewport (user scrolled back UP into the gallery/title area),
          // keep the sticky bar hidden — showing it there makes no sense.
          const rect = entry.boundingClientRect;
          const isAboveViewport = rect.bottom <= 0;
          if (isAboveViewport) {
            // Reveal the element FIRST (still in hidden transform state),
            // then flip data-visible on the next frame so the slide-in
            // transition starts cleanly from off-screen. Setting both at
            // once can make the bar flash at the wrong position for a frame.
            this.hidden = false;
            this.setAttribute('aria-hidden', 'false');
            if (this._showRaf) cancelAnimationFrame(this._showRaf);
            this._showRaf = requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (this._hasSeenButton) this.dataset.visible = 'true';
              });
            });
          } else {
            // Button is below the viewport — user is above the cart area
            if (this._showRaf) { cancelAnimationFrame(this._showRaf); this._showRaf = null; }
            this.dataset.visible = 'false';
            this.setAttribute('aria-hidden', 'true');
          }
        }
        // If !isIntersecting && !_hasSeenButton → do nothing (user hasn't scrolled down yet)
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.observer.observe(this.mainAtcButton);

    // Listen for variant updates
    document.addEventListener('variant:update', this.handleVariantUpdate);

    // Form submit
    const form = this.querySelector('.sticky-atc__form');
    if (form) {
      form.addEventListener('submit', this.handleSubmit);
    }

    // Also push chat/back-to-top via JS as fallback for browsers without :has()
    this._pushElementsInterval = null;
    this._setupMobilePush();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    document.removeEventListener('variant:update', this.handleVariantUpdate);

    const form = this.querySelector('.sticky-atc__form');
    if (form) form.removeEventListener('submit', this.handleSubmit);

    if (this._showRaf) { cancelAnimationFrame(this._showRaf); this._showRaf = null; }

    // Restore pushed elements
    this._restoreMobilePush();
  }

  /**
   * JS fallback to push chat widget & back-to-top button up on mobile
   * For browsers that don't support :has() selector
   */
  _setupMobilePush() {
    if (window.innerWidth >= 750) return;

    const barHeight = 70; // approximate bar height

    // MutationObserver to catch dynamically loaded chat widgets
    this._mobilePushObserver = new MutationObserver(() => {
      if (this.dataset.visible === 'true') {
        this._pushElements(barHeight);
      }
    });
    this._mobilePushObserver.observe(document.body, { childList: true, subtree: true });

    // Observe data-visible changes
    const attrObserver = new MutationObserver(() => {
      if (this.dataset.visible === 'true') {
        this._pushElements(barHeight);
      } else {
        this._restoreElements();
      }
    });
    attrObserver.observe(this, { attributes: true, attributeFilter: ['data-visible'] });
    this._attrObserver = attrObserver;
  }

  _pushElements(offset) {
    // Back-to-top button
    const backToTop = document.querySelector('.biwo-footer__back-to-top');
    if (backToTop) {
      backToTop.style.setProperty('bottom', `${28 + offset}px`, 'important');
    }

    // Chat widgets (Shopify Inbox or others)
    const chatSelectors = [
      '#shopify-chat', '#ShopifyChat', '.shopify-chat',
      'iframe[title*="chat" i]', 'iframe[title*="Shopify" i]',
      '[id*="chat-widget"]', '[class*="chat-widget"]'
    ];
    chatSelectors.forEach(sel => {
      const el = document.querySelector(sel);
      if (el) {
        el.style.setProperty('bottom', `${offset}px`, 'important');
        el.dataset.stickyAtcPushed = 'true';
      }
    });
  }

  _restoreElements() {
    const backToTop = document.querySelector('.biwo-footer__back-to-top');
    if (backToTop) {
      backToTop.style.removeProperty('bottom');
    }

    document.querySelectorAll('[data-sticky-atc-pushed]').forEach(el => {
      el.style.removeProperty('bottom');
      delete el.dataset.stickyAtcPushed;
    });
  }

  _restoreMobilePush() {
    this._restoreElements();
    if (this._mobilePushObserver) this._mobilePushObserver.disconnect();
    if (this._attrObserver) this._attrObserver.disconnect();
  }

  handleVariantUpdate(event) {
    const { resource: variant, data } = event.detail || {};

    // Only react to updates for this product
    if (data?.productId && String(data.productId) !== String(this.dataset.productId)) return;

    const variantIdInput = this.querySelector('[data-sticky-variant-id]');
    const priceEl = this.querySelector('[data-sticky-price-current]');
    const comparePriceEl = this.querySelector('[data-sticky-price-compare]');
    const variantInfoEl = this.querySelector('[data-sticky-variant-info]');
    const button = this.querySelector('[data-sticky-atc-btn]');
    const btnText = this.querySelector('[data-sticky-btn-text]');
    const imageEl = this.querySelector('.sticky-atc__image img');

    if (!variant) {
      if (button) button.disabled = true;
      return;
    }

    // Update variant ID
    if (variantIdInput) variantIdInput.value = variant.id;

    // Update button state
    if (button) button.disabled = !variant.available;
    if (btnText) {
      // Try to get translation from the main page's button
      const mainBtn = document.querySelector(
        'product-form-component [ref="addToCartButton"] [ref="addToCartText"]'
      );
      if (mainBtn) {
        btnText.textContent = mainBtn.textContent.trim();
      }
    }

    // Update price from the re-rendered HTML in the event
    if (data?.html && priceEl) {
      const newDoc = data.html;

      // Try multiple selectors to find the price
      const priceSelectors = [
        'product-price .price--current .money',
        'product-price [ref="price"] .money',
        '.price .money',
        'product-price .money'
      ];

      for (const sel of priceSelectors) {
        const found = newDoc.querySelector(sel);
        if (found) {
          priceEl.textContent = found.textContent.trim();
          break;
        }
      }

      // Compare at price
      const compareSelectors = [
        'product-price .price--compare-at .money',
        'product-price [ref="compareAtPrice"] .money',
        '.price--compare-at .money'
      ];

      let foundCompare = null;
      for (const sel of compareSelectors) {
        foundCompare = newDoc.querySelector(sel);
        if (foundCompare) break;
      }

      const priceContainer = this.querySelector('[data-sticky-price]');

      if (comparePriceEl) {
        if (foundCompare) {
          comparePriceEl.textContent = foundCompare.textContent.trim();
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
        }
      } else if (foundCompare && priceContainer) {
        const span = document.createElement('span');
        span.className = 'sticky-atc__price-compare';
        span.dataset.stickyPriceCompare = '';
        span.textContent = foundCompare.textContent.trim();
        priceContainer.appendChild(span);
      }
    }

    // Update variant info text
    if (variantInfoEl && variant.title && variant.title !== 'Default Title') {
      variantInfoEl.textContent = variant.title;
    }

    // Update image
    if (imageEl && variant.featured_media?.preview_image?.src) {
      const src = variant.featured_media.preview_image.src;
      const cleanSrc = src.includes('?') ? src.split('?')[0] : src;
      imageEl.src = cleanSrc + '?width=100';
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('.sticky-atc__form');
    const button = this.querySelector('[data-sticky-atc-btn]');
    if (!form || !button || button.disabled) return;

    // Set loading state
    button.dataset.loading = 'true';
    button.disabled = true;

    const formData = new FormData(form);

    // Sync quantity from main form
    const mainQuantityInput = document.querySelector(
      'product-form-component input[name="quantity"]'
    ) || document.querySelector(
      'quantity-selector-component input[name="quantity"]'
    );
    if (mainQuantityInput && mainQuantityInput.value) {
      formData.set('quantity', mainQuantityInput.value);
    }

    // Also sync any line item properties from the main form (e.g., Zuschnitt properties)
    const mainForm = document.querySelector('product-form-component form');
    if (mainForm) {
      const mainFormData = new FormData(mainForm);
      for (const [key, value] of mainFormData.entries()) {
        if (key.startsWith('properties[') && !formData.has(key)) {
          formData.set(key, value);
        }
      }
    }

    const cartAddUrl = window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart/add.js`
      : '/cart/add.js';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });

      const result = await response.json();

      if (result.status) {
        // Error from Shopify
        console.error('Sticky ATC error:', result.description);
        button.dataset.loading = 'false';
        button.disabled = false;
        return;
      }

      // Success
      button.dataset.loading = 'false';
      button.classList.add('is-added');

      // Dispatch cart:update event so header cart icon & drawer updates
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: null,
          sourceId: `sticky-atc-${this.dataset.sectionId}`,
          data: {
            source: 'product-form',
            productId: this.dataset.productId,
            variantId: String(formData.get('id')),
            itemCount: null
          }
        }
      }));

      // Reset after 2s
      setTimeout(() => {
        button.classList.remove('is-added');
        button.disabled = false;
      }, 2000);

    } catch (err) {
      console.error('Sticky ATC fetch error:', err);
      button.dataset.loading = 'false';
      button.disabled = false;
    }
  }
}

if (!customElements.get('sticky-atc-bar')) {
  customElements.define('sticky-atc-bar', StickyAtcBar);
}
